import { useState, useEffect } from 'react';
import Linkify from 'react-linkify';
import EmojiPicker from 'emoji-picker-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { 
  Plus, Search, Users, MessageSquare, Pin, X, Send, Smile, Upload, LogIn, ChevronRight, Settings,
  User, MoreVertical, Ban, MessageCircle, Shield, Volume2, VolumeX, UserMinus, LogOut as LeaveIcon, Flag, Phone
} from 'lucide-react';
import { API } from '../../App'; // Import API for fetching fonts
import { getImageUrl } from '../../utils/imageUtils';
import { getRoleBadge, renderUserBadges } from '../../components/chat/ChatMessage';
import { useNavigate } from 'react-router-dom';
import { Label } from '../../components/ui/label';
import { applyCustomFont } from '../../utils/fontUtils'; // Import the shared utility
import axios from 'axios';
import MembersModal from '../../components/chat/modals/MembersModal';
import LudoTab from '../../components/chat/LudoTab';
import useVoipMesh from '../../hooks/useVoipMesh';


const isImageUrl = (text) => {
  if (!text || typeof text !== 'string') return false;
  // Check for common image file extensions
  if (/\.(jpeg|jpg|gif|png|webp)$/i.test(text)) {
      return true;
  }
  // If it's an absolute URL, assume it's an image if it has http/https
  // getImageUrl will handle the absolute nature and not prepend API_BASE_URL
  if (text.startsWith('http://') || text.startsWith('https://')) {
      return true;
  }
  return false;
}

/**
 * Mobile Layout for ChatPage
 */
const MobileChatLayout = ({ user, state, api, pmWindows, messageHandlers, secureUrl, userRoomRole, canLeaveRoom }) => {
  const navigate = useNavigate();
  const [roomsTabSection, setRoomsTabSection] = useState('all');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [customFonts, setCustomFonts] = useState([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [roomCallInfo, setRoomCallInfo] = useState(null);
  const [roomInCall, setRoomInCall] = useState(false);
  const [roomMuted, setRoomMuted] = useState(false);
  
  const {
    rooms, selectedRoom, messages, members,
    mobileNavTab, setMobileNavTab,
    activeTab, setActiveTab,
    roomSearchQuery, setRoomSearchQuery,
    pinnedMessages, showPinned, setShowPinned,
    messageInput, setMessageInput,
    replyingTo, setReplyingTo,
    showEmojiPicker, setShowEmojiPicker,
    showGiphyPicker, setShowGiphyPicker,
    giphySearch, setGiphySearch,
    giphyResults,
    showMentionDropdown, mentionResults,
    showInputOptions, setShowInputOptions,
            globalTheme, roomTheme,    messagesEndRef, messageInputRef, fileInputRef,
    ws,
    unreadPmCount, unreadBySender,
    friends, friendRequests, pmRequests, staffList, blockedUsers,
    friendsTabSection, setFriendsTabSection,
    userSearchQuery, setUserSearchQuery, userSearchResults,
    setLightboxMedia,
    filteredRooms, pendingJoinRoom,
    newRoomName, setNewRoomName,
    newRoomDescription, setNewRoomDescription,
    isPrivateRoom, setIsPrivateRoom,
    showMembersModal, setShowMembersModal,
    createRoomOpen, setCreateRoomOpen,
    newRoomData, setNewRoomData
  } = state;

  const { 
    selectRoom, handleJoinRoom, createRoomMobile, createRoom, leaveRoom,
    searchUsers, sendFriendRequest, acceptFriendRequest, rejectFriendRequest,
    acceptPmRequest, rejectPmRequest, blockUser, unblockUser, unfriendUser,
    pinMessage, unpinMessage, deleteMessage, viewProfile
  } = api;

  const {
    handleMessageInputChange, selectMention, sendMessage, sendEmoji,
    onEmojiClick, searchGiphy, selectGif, handleFileUpload, handleAvatarClick
  } = messageHandlers;

  const { linkDecorator } = secureUrl;

  const canModerate = (role) => {
    if (user.role === 'super_admin' || user.role === 'staff') return true;
    return ['owner', 'admin', 'moderator'].includes(role);
  };

  const startRoomCall = async () => {
    if (!selectedRoom?.id) return;
    try {
      const res = await axios.post(`${API}/voip/start`, {
        scope: 'room',
        target_id: selectedRoom.id
      });
      const joinLink = res?.data?.join_link || '';
      setRoomCallInfo({
        id: res?.data?.call_id,
        host_id: user.id,
        participants: [user.id],
        status: 'active'
      });
      setRoomInCall(true);
      import('sonner').then(({ toast }) => {
        toast.success(joinLink ? `Room call started (${joinLink})` : 'Room call started');
      });
    } catch (error) {
      import('sonner').then(({ toast }) => {
        toast.error(error?.response?.data?.detail || 'Unable to start room call');
      });
    }
  };

  const endRoomCall = async () => {
    if (!roomCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${roomCallInfo.id}/end`);
      const peers = (roomCallInfo.participants || []).filter((pid) => pid && pid !== user.id);
      if (ws && ws.readyState === WebSocket.OPEN) {
        peers.forEach((peerId) => {
          ws.send(JSON.stringify({
            type: 'voip_call_end',
            call_id: roomCallInfo.id,
            scope: 'room',
            target_id: selectedRoom?.id,
            recipient_id: peerId
          }));
        });
      }
      setRoomInCall(false);
      setRoomMuted(false);
      setRoomCallInfo(null);
      import('sonner').then(({ toast }) => toast.success('Group call ended'));
    } catch (error) {
      import('sonner').then(({ toast }) => toast.error(error?.response?.data?.detail || 'Unable to end room call'));
    }
  };

  const loadActiveRoomCall = async () => {
    if (!selectedRoom?.id || user?.role === 'guest') {
      setRoomCallInfo(null);
      setRoomInCall(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/voip/active`, {
        params: { scope: 'room', target_id: selectedRoom.id }
      });
      if (response.data?.active && response.data?.call) {
        setRoomCallInfo(response.data.call);
        setRoomInCall(Boolean(response.data.is_participant));
      } else {
        setRoomCallInfo(null);
        setRoomInCall(false);
      }
    } catch {
      setRoomCallInfo(null);
      setRoomInCall(false);
    }
  };

  const joinRoomCall = async () => {
    if (!roomCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${roomCallInfo.id}/join`);
      setRoomInCall(true);
      import('sonner').then(({ toast }) => toast.success('Joined room call'));
      loadActiveRoomCall();
    } catch (error) {
      import('sonner').then(({ toast }) => toast.error(error?.response?.data?.detail || 'Unable to join room call'));
    }
  };

  const leaveRoomCall = async () => {
    if (!roomCallInfo?.id) return;
    try {
      await axios.post(`${API}/voip/${roomCallInfo.id}/leave`);
      setRoomInCall(false);
      setRoomMuted(false);
      import('sonner').then(({ toast }) => toast.success('Left room call'));
      loadActiveRoomCall();
    } catch (error) {
      import('sonner').then(({ toast }) => toast.error(error?.response?.data?.detail || 'Unable to leave room call'));
    }
  };

  const isMobileViewport = typeof window !== 'undefined' ? window.innerWidth < 768 : true;
  const { remoteStreams: roomRemoteStreams, setMuted: setRoomVoipMuted } = useVoipMesh({
    ws,
    user,
    enabled: Boolean(isMobileViewport && roomInCall && roomCallInfo?.id),
    callId: roomCallInfo?.id,
    scope: 'room',
    targetId: selectedRoom?.id,
    peerIds: roomCallInfo?.participants || []
  });

  useEffect(() => {
    setRoomVoipMuted(roomMuted);
  }, [roomMuted, setRoomVoipMuted]);

  useEffect(() => {
    if (!ws) return;
    const onMessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'voip_call_end' && data.call_id && roomCallInfo?.id === data.call_id) {
        setRoomInCall(false);
        setRoomMuted(false);
        setRoomCallInfo(null);
      }
    };
    ws.addEventListener('message', onMessage);
    return () => ws.removeEventListener('message', onMessage);
  }, [ws, roomCallInfo?.id]);

  // Load custom fonts
  useEffect(() => {
      const fetchCustomFonts = async () => {
          try {
              // Use the public endpoint
              const response = await axios.get(`${API}/public/fonts`);
              if (response.status === 200) { // Check status for axios
                  const data = response.data; // Axios returns data directly
                  setCustomFonts(data);
              }
          } catch (error) {
              console.error('Failed to load fonts:', error);
          }
      };
      fetchCustomFonts();
  }, []);

  // Apply font when room changes
  useEffect(() => {
      if (selectedRoom && roomTheme?.font_id && customFonts.length > 0) {
          const selectedFont = customFonts.find(f => f.id === roomTheme.font_id || f.name === roomTheme.font_family);
          if (selectedFont) {
              applyCustomFont(selectedFont.name || selectedFont.font_family, selectedFont.url, selectedFont.format);
          }
      }
  }, [selectedRoom, roomTheme, customFonts]);

  // Report user function
  const handleReportUser = async () => {
    if (!reportTarget || !reportReason.trim()) return;
    
    setReportSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reported_user_id: reportTarget.id,
          room_id: selectedRoom?.id || null,
          reason: reportReason
        })
      });
      
      if (response.ok) {
        import('sonner').then(({ toast }) => {
          toast.success('Report submitted successfully');
        });
        setReportModalOpen(false);
        setReportTarget(null);
        setReportReason('');
      } else {
        const data = await response.json();
        import('sonner').then(({ toast }) => {
          toast.error(data.detail || 'Failed to submit report');
        });
      }
    } catch (error) {
      console.error('Report error:', error);
      import('sonner').then(({ toast }) => {
        toast.error('Failed to submit report');
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  // Open report modal for a user
  const openReportModal = (memberId, memberUsername) => {
    setReportTarget({ id: memberId, username: memberUsername });
    setReportModalOpen(true);
  };

  // Load trending GIFs when picker opens
  useEffect(() => {
    if (showGiphyPicker && giphyResults.length === 0) {
      searchGiphy(''); // Empty query triggers trending
    }
  }, [showGiphyPicker]);

  useEffect(() => {
    loadActiveRoomCall();
    const id = setInterval(loadActiveRoomCall, 8000);
    return () => clearInterval(id);
  }, [selectedRoom?.id, user?.role]);

  // Filter rooms for mobile search
  const mobileFilteredRooms = rooms.filter((room) =>
    room.name.toLowerCase().includes(roomSearchQuery.toLowerCase())
  );

  return (
    <div className="md:hidden flex-1 flex flex-col overflow-hidden">
      {/* Rooms Tab */}
      {mobileNavTab === 'rooms' && (
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
          {/* Room subtabs */}
          <div className="flex gap-1 p-2 border-b bg-gray-50 dark:bg-gray-800">
            {['all', 'create', 'my rooms'].map((section) => (
              <Button
                key={section}
                variant={roomsTabSection === section ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRoomsTabSection(section)}
                className="text-xs px-3 flex-1"
              >
                {section === 'all' ? 'All' : section === 'create' ? 'Create' : 'My Rooms'}
              </Button>
            ))}
          </div>
          
          {/* All Rooms */}
          {roomsTabSection === 'all' && (
            <>
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search rooms..."
                    value={roomSearchQuery}
                    onChange={(e) => setRoomSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <ScrollArea className="flex-1">
                {mobileFilteredRooms.map((room) => {
                  const isMember = room.is_member || room.owner_id === user.id;
                  const isOwner = room.owner_id === user.id;
                  const isGlobalChat = room.name?.toLowerCase() === 'global chat' || room.name?.toLowerCase() === 'public chat';
                  
                  return (
                    <div
                      key={room.id}
                      className={`p-3 border-b flex items-center justify-between ${selectedRoom?.id === room.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                    >
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => {
                          selectRoom(room);
                          setMobileNavTab('chats');
                        }}
                      >
                        <div className="font-medium flex items-center gap-2">
                          {room.name}
                          {isMember ? (
                            <Badge className="text-[10px] bg-green-600">Joined</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">Left</Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{room.type} • {room.member_count || 0} members</div>
                      </div>
                      <div className="flex items-center gap-1">
                        {isMember && !isOwner && !isGlobalChat ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="px-2 h-8 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Leave ${room.name}?`)) {
                                leaveRoom(room.id);
                              }
                            }}
                            title="Leave Room"
                          >
                            Leave
                          </Button>
                        ) : !isMember ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="px-2 h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleJoinRoom(room);
                            }}
                            title="Join Room"
                          >
                            Join
                          </Button>
                        ) : null}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  );
                })}
              </ScrollArea>
            </>
          )}
          
          {/* Create Room */}
          {roomsTabSection === 'create' && user.role !== 'guest' && (
            <div className="flex-1 p-4">
              <h3 className="font-bold text-lg mb-4">Create New Room</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Room Name</label>
                  <Input
                    placeholder="Enter room name..."
                    value={newRoomData?.name || newRoomName || ''}
                    onChange={(e) => {
                      if (setNewRoomData) {
                        setNewRoomData(prev => ({ ...prev, name: e.target.value }));
                      } else {
                        setNewRoomName(e.target.value);
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Description (optional)</label>
                  <Input
                    placeholder="Room description..."
                    value={newRoomData?.description || newRoomDescription || ''}
                    onChange={(e) => {
                      if (setNewRoomData) {
                        setNewRoomData(prev => ({ ...prev, description: e.target.value }));
                      } else {
                        setNewRoomDescription(e.target.value);
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Room Type</label>
                  <div className="flex gap-2">
                    <Button
                      variant={(!newRoomData?.type || newRoomData?.type === 'public') && !isPrivateRoom ? 'default' : 'outline'}
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        if (setNewRoomData) {
                          setNewRoomData(prev => ({ ...prev, type: 'public' }));
                        }
                        if (setIsPrivateRoom) setIsPrivateRoom(false);
                      }}
                    >
                      Public
                    </Button>
                    <Button
                      variant={(newRoomData?.type === 'private' || isPrivateRoom) ? 'default' : 'outline'}
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        if (setNewRoomData) {
                          setNewRoomData(prev => ({ ...prev, type: 'private' }));
                        }
                        if (setIsPrivateRoom) setIsPrivateRoom(true);
                      }}
                    >
                      Private
                    </Button>
                  </div>
                </div>
                <Button 
                  onClick={() => {
                    if (createRoom) {
                      createRoom();
                    } else if (createRoomMobile) {
                      createRoomMobile();
                    }
                    setRoomsTabSection('all');
                  }} 
                  className="w-full mt-4" 
                  disabled={!(newRoomData?.name || newRoomName)?.trim()}
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Room
                </Button>
              </div>
            </div>
          )}
          {roomsTabSection === 'create' && user.role === 'guest' && (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center text-gray-500">
                <p>Guests cannot create rooms.</p>
                <p className="text-sm mt-2">Please register to create rooms.</p>
              </div>
            </div>
          )}
          
          {/* My Rooms - Only rooms created by the user */}
          {roomsTabSection === 'my rooms' && (
            <>
              <div className="p-3 border-b bg-blue-50 dark:bg-blue-900/20">
                <p className="text-sm text-blue-700 dark:text-blue-300">Rooms you&apos;ve created</p>
              </div>
              <ScrollArea className="flex-1">
                {rooms.filter(room => room.owner_id === user.id).length > 0 ? (
                  rooms.filter(room => room.owner_id === user.id).map((room) => {
                    const isGlobalChat = room.name?.toLowerCase() === 'global chat' || room.name?.toLowerCase() === 'public chat';
                    
                    return (
                      <div
                        key={room.id}
                        className={`p-3 border-b flex items-center justify-between ${selectedRoom?.id === room.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                      >
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => {
                            selectRoom(room);
                            setMobileNavTab('chats');
                          }}
                        >
                          <div className="font-medium flex items-center gap-2">
                            {room.name}
                            <Badge variant="outline" className="text-[10px] px-1 bg-green-50 text-green-700">Owner</Badge>
                          </div>
                          <div className="text-xs text-gray-500">{room.type} â€¢ {room.member_count || 0} members</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectRoom(room);
                              setMobileNavTab('chats');
                              setTimeout(() => state.setRoomSettingsOpen(true), 300);
                            }}
                            title="Room Settings"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <p>You haven&apos;t created any rooms yet</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => setRoomsTabSection('create')}
                    >
                      Create a Room
                    </Button>
                  </div>
                )}
              </ScrollArea>
            </>
          )}
        </div>
      )}

      {/* Friends Tab */}
      {mobileNavTab === 'friends' && (
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
          <div className="p-3 border-b">
            <div className="flex gap-2">
              <Input
                placeholder="Search users..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchUsers(userSearchQuery)}
                className="flex-1"
              />
              <Button onClick={() => searchUsers(userSearchQuery)} size="sm">Search</Button>
            </div>
          </div>
          <div className="flex gap-1 p-2 border-b overflow-x-auto">
            {['friends', 'staff', 'pending', 'blocked'].map((section) => (
              <Button
                key={section}
                variant={friendsTabSection === section ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFriendsTabSection(section)}
                className="text-xs px-2"
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
                {section === 'pending' && friendRequests.length > 0 && (
                  <Badge className="ml-1 bg-red-500 text-[10px] px-1">{friendRequests.length}</Badge>
                )}
              </Button>
            ))}
          </div>
          <ScrollArea className="flex-1">
            {userSearchResults.length > 0 && (
              <div className="p-2 bg-blue-50">
                <p className="text-xs font-medium mb-2">Search Results</p>
                {userSearchResults.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-2 bg-white rounded mb-1">
                    <span>{u.username}</span>
                    <Button size="sm" onClick={() => sendFriendRequest(u.id)}>Add</Button>
                  </div>
                ))}
              </div>
            )}
            {friendsTabSection === 'friends' && friends.map((friend) => (
              <div key={friend.id} className="p-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={getImageUrl(friend.avatar_url)} />
                      <AvatarFallback>{friend.username?.substring(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white ${
                      friend.is_online || friend.online ? 'bg-green-500' : 'bg-gray-400'
                    }`} />
                  </div>
                  <div>
                    <span className="font-medium">{friend.display_name || friend.username}</span>
                    <p className="text-xs text-gray-500">{friend.is_online || friend.online ? 'Online' : 'Offline'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {/* View Profile Icon */}
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => viewProfile(friend.id)}
                    className="px-2"
                    title="View Profile"
                  >
                    <User className="w-4 h-4 text-blue-500" />
                  </Button>
                  {/* PM Icon */}
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => pmWindows.openPMWindow(friend)}
                    className="px-2"
                    title="Send Message"
                  >
                    <MessageCircle className="w-4 h-4 text-green-500" />
                  </Button>
                  {/* Unfriend Icon */}
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => {
                      if (window.confirm(`Unfriend ${friend.username}?`)) {
                        unfriendUser(friend.id);
                      }
                    }}
                    className="px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                    title="Unfriend"
                  >
                    <UserMinus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {friendsTabSection === 'staff' && (
              <>
                {staffList && staffList.length > 0 ? (
                  staffList.map((staff) => (
                    <div key={staff.id} className="p-3 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={getImageUrl(staff.avatar_url)} />
                          <AvatarFallback className="bg-purple-600 text-white">{staff.username?.substring(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <span className="font-medium">{staff.username}</span>
                          <p className="text-xs text-purple-600">{staff.role === 'super_admin' ? 'Admin' : 'Staff'}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => pmWindows.openPMWindow(staff)}>PM</Button>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <p>No staff members available</p>
                  </div>
                )}
              </>
            )}
            {friendsTabSection === 'pending' && friendRequests.map((req) => (
              <div key={req.id} className="p-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={getImageUrl(req.from_user?.avatar_url)} />
                    <AvatarFallback>{(req.from_user?.username || req.username)?.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span>{req.from_user?.username || req.username}</span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => acceptFriendRequest(req.id)}>Accept</Button>
                  <Button size="sm" variant="outline" onClick={() => rejectFriendRequest(req.id)}>Reject</Button>
                </div>
              </div>
            ))}
            {friendsTabSection === 'blocked' && (
              <>
                {blockedUsers && blockedUsers.length > 0 ? (
                  blockedUsers.map((blocked) => (
                    <div key={blocked.id} className="p-3 border-b flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={getImageUrl(blocked.avatar_url)} />
                          <AvatarFallback>{blocked.username?.substring(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span>{blocked.username}</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => unblockUser(blocked.id)}>Unblock</Button>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <p>No blocked users</p>
                  </div>
                )}
              </>
            )}
            {friendsTabSection === 'friends' && friends.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p>No friends yet. Search for users to add!</p>
              </div>
            )}
            {friendsTabSection === 'pending' && friendRequests.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <p>No pending requests</p>
              </div>
            )}
          </ScrollArea>
        </div>
      )}

      {/* Chats Tab (Main Chat) */}
      {mobileNavTab === 'chats' && (
        <>
          {selectedRoom ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Mobile Chat Header */}
              <div className="p-3 border-b bg-white dark:bg-gray-800 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-gray-800 dark:text-gray-100">{selectedRoom.name}</h2>
                  <p className="text-xs text-green-600 font-medium">
                    {members.filter(m => m.online).length} online
                  </p>
                </div>
                <div className="flex gap-1">
                  {pinnedMessages.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setShowPinned(!showPinned)}>
                      <Pin className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => state.setShowMembersModal(true)}>
                    <Users className="w-4 h-4" />
                    <span className="ml-1 text-xs">{members.length}</span>
                  </Button>
                  {canModerate(userRoomRole) && (
                    <Button variant="ghost" size="sm" onClick={startRoomCall} title="Start group call">
                      <Phone className="w-4 h-4" />
                    </Button>
                  )}
                  {canModerate(userRoomRole) && (
                    <Button variant="ghost" size="sm" onClick={() => state.setRoomSettingsOpen(true)}>
                      <Settings className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {roomCallInfo && (
                <div className="px-3 py-2 border-b bg-blue-50 dark:bg-gray-700 dark:border-gray-600 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${roomInCall ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-300'}`}>
                      {roomInCall ? 'In call' : 'Call active'}
                    </span>
                    <span className="text-gray-600 dark:text-gray-300">
                      {(roomCallInfo.participants || []).length}
                    </span>
                    <span className={`font-medium ${roomMuted ? 'text-orange-600 dark:text-orange-400' : 'text-gray-600 dark:text-gray-300'}`}>
                      {roomMuted ? 'Muted' : 'Unmuted'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {Object.entries(roomRemoteStreams).map(([peerId, stream]) => (
                      <audio
                        key={peerId}
                        autoPlay
                        playsInline
                        ref={(el) => {
                          if (!el) return;
                          if (el.srcObject !== stream) {
                            el.srcObject = stream;
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    ))}
                    {roomInCall && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => setRoomMuted((prev) => !prev)}
                      >
                        {roomMuted ? 'Unmute' : 'Mute'}
                      </Button>
                    )}
                    {roomInCall && roomCallInfo?.host_id === user.id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs text-red-600"
                        onClick={endRoomCall}
                      >
                        End
                      </Button>
                    )}
                    <Button
                      variant={roomInCall ? 'outline' : 'default'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={roomInCall ? leaveRoomCall : joinRoomCall}
                    >
                      {roomInCall ? 'Leave' : 'Join'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Pinned Messages */}
              {showPinned && pinnedMessages.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 max-h-48 overflow-y-auto border-b border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 flex items-center gap-1">
                      <Pin className="w-3 h-3" /> Pinned Messages ({pinnedMessages.length})
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setShowPinned(false)} className="h-5 w-5 p-0">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  {pinnedMessages.map((msg) => (
                    <div key={msg.id} className="text-xs p-2 bg-white dark:bg-gray-800 rounded mb-1 shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-gray-700 dark:text-gray-300">{msg.sender?.username}: </span>
                          <div className="text-gray-600 dark:text-gray-400 break-words whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>
                            {isImageUrl(msg.content) ? (
                                <>
                                    <img
                                        src={getImageUrl(msg.content)}
                                        alt="Pinned image"
                                        style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '8px', marginTop: '5px' }}
                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                    />
                                    <span style={{ display: 'none' }}>{msg.content}</span>
                                </>
                            ) : (
                                <span>{msg.content}</span>
                            )}
                          </div>
                        </div>
                        {canModerate(userRoomRole) && (
                          <button 
                            onClick={() => unpinMessage(msg.id)}
                            className="flex-shrink-0 p-1 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 rounded"
                            title="Unpin"
                          >
                            <X className="w-3 h-3 text-yellow-600" />
                          </button>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400 mt-1 block">
                        {new Date(msg.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Messages */}
              <ScrollArea 
                className="flex-1 p-3"
                style={{
                  backgroundImage: selectedRoom?.background_url 
                    ? `url(${selectedRoom.background_url.startsWith('http') ? selectedRoom.background_url : API.replace('/api', '') + selectedRoom.background_url})` 
                    : 'linear-gradient(to bottom right, rgb(248 250 252), rgb(239 246 255))',
                  backgroundSize: 'cover'
                }}
              >
                <div className="space-y-3">
                  {messages.map((msg, idx) => (
                    <div key={msg.id || idx}>
                      {msg.type === 'system' ? (
                        <div className="text-center text-xs text-gray-500 py-1">{msg.content}</div>
                      ) : (
                        <div className={`flex gap-2 ${msg.sender_id === user.id ? 'flex-row-reverse' : ''}`}>
                          <Avatar 
                            className={`w-8 h-8 flex-shrink-0 ${msg.sender_id !== user.id ? 'cursor-pointer' : ''}`}
                            onClick={() => msg.sender_id !== user.id && handleAvatarClick(msg.sender?.username, msg.sender_id)}
                          >
                            <AvatarImage src={getImageUrl(msg.sender?.avatar_url)} />
                            <AvatarFallback className="bg-blue-600 text-white text-xs">
                              {msg.sender?.username?.substring(0, 2).toUpperCase() || '??'}
                            </AvatarFallback>
                          </Avatar>
                          <div className={`flex flex-col max-w-[75%] ${msg.sender_id === user.id ? 'items-end' : ''}`}>
                            <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                              <span className="text-xs font-medium text-gray-700">{msg.sender?.display_name || msg.sender?.username}</span>
                              {getRoleBadge(msg.sender?.role)}
                              <span className="text-[10px] text-gray-400">
                                {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </span>
                            </div>
                            {msg.parent_message && (
                              <div className="text-[10px] mb-1 p-1.5 rounded border-l-2 border-gray-300 bg-gray-50">
                                <div className="font-semibold">Reply to {msg.parent_message.sender?.username}:</div>
                                <div className="truncate opacity-75">{msg.parent_message.content.substring(0, 30)}...</div>
                              </div>
                            )}
                            <div
                              className={`px-3 py-2 rounded-2xl ${msg.sender_id === user.id ? '' : 'border border-gray-200'}`}
                              style={{
                                backgroundColor: msg.sender_id === user.id ? (globalTheme.chat_bubble_own || '#2563EB') : (globalTheme.chat_bubble_other || '#FFFFFF'),
                                color: msg.sender_id === user.id ? '#ffffff' : (globalTheme.font_color || '#000000'),
                                fontFamily: roomTheme.font_family || 'inherit', // Apply custom font
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word'
                              }}
                            >
                              {msg.content.startsWith('http') && msg.content.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                <img src={getImageUrl(msg.content)} alt="media" className="rounded max-w-[150px]" onClick={() => setLightboxMedia({ url: getImageUrl(msg.content), type: 'image' })} />
                              ) : msg.content.startsWith('http') && msg.content.match(/\.(mp4|webm)$/i) ? (
                                <video src={getImageUrl(msg.content)} className="rounded max-w-[150px]" controls />
                              ) : (
                                <Linkify componentDecorator={linkDecorator}>
                                  <span className="text-sm whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>{msg.content}</span>
                                </Linkify>
                              )}
                            </div>
                            <div className="flex gap-2 mt-1 flex-wrap">
                              <button onClick={() => setReplyingTo(msg)} className="text-xs text-blue-600">Reply</button>
                              {canModerate(userRoomRole) && !msg.pinned && (
                                <button onClick={() => pinMessage(msg.id)} className="text-xs text-yellow-600">Pin</button>
                              )}
                              {(msg.sender_id === user.id || canModerate(userRoomRole)) && (
                                <button onClick={() => deleteMessage(msg.id)} className="text-xs text-red-600">Delete</button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Reply indicator */}
              {replyingTo && (
                <div className="bg-blue-50 border-t px-3 py-2 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-blue-700 font-medium">Replying to {replyingTo.sender?.username}</p>
                    <p className="text-xs text-gray-600 truncate">{replyingTo.content.substring(0, 50)}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setReplyingTo(null)} className="p-1">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Message Input */}
              <div className="p-2 border-t bg-white dark:bg-gray-800">
                {showInputOptions && (
                  <div className="flex gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                    <Button size="sm" variant="outline" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                      <Smile className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setShowGiphyPicker(!showGiphyPicker)}>
                      GIF
                    </Button>
                    <label className="cursor-pointer">
                      <Button size="sm" variant="outline" asChild>
                        <span><Upload className="w-4 h-4" /></span>
                      </Button>
                      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,video/*" />
                    </label>
                  </div>
                )}
                {showEmojiPicker && (
                  <div className="absolute bottom-20 left-2 z-50">
                    <EmojiPicker onEmojiClick={(emoji) => { 
                      sendEmoji(emoji.emoji); 
                      setShowEmojiPicker(false); 
                    }} />
                  </div>
                )}
                {showGiphyPicker && (
                  <div className="absolute bottom-20 left-2 z-50 bg-white border rounded-lg shadow-lg p-3 w-72">
                    <Input
                      placeholder="Search GIFs..."
                      value={giphySearch}
                      onChange={(e) => setGiphySearch(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && searchGiphy(giphySearch)}
                      className="mb-2"
                    />
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {giphyResults.map((gif, i) => (
                        <img
                          key={gif.id || i}
                          src={getImageUrl(gif.images?.fixed_height_small?.url)}
                          alt="GIF"
                          className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={() => { selectGif(gif.images?.original?.url); setShowGiphyPicker(false); setShowInputOptions(false); }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={() => setShowInputOptions(!showInputOptions)}
                    className={showInputOptions ? 'bg-blue-50 text-blue-600' : ''}
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                  <Input
                    ref={messageInputRef}
                    placeholder="Message..."
                    value={messageInput}
                    onChange={(e) => handleMessageInputChange(e)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    className="flex-1"
                  />
                  <Button onClick={sendMessage} disabled={!messageInput.trim()}>
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              <div className="text-center p-4">
                <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <h3 className="font-semibold text-gray-700 dark:text-gray-300">No Chat Selected</h3>
                <p className="text-sm text-gray-500 mt-1">Select a room from the Rooms tab</p>
                <Button className="mt-4" onClick={() => setMobileNavTab('rooms')}>Browse Rooms</Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Notifications Tab */}
      {mobileNavTab === 'ludo' && (
        <div className="flex-1 bg-white dark:bg-gray-900 overflow-hidden">
          <LudoTab user={user} ws={ws} />
        </div>
      )}

      {/* Notifications Tab */}
      {mobileNavTab === 'notifications' && (
        <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
          <div className="p-3 border-b">
            <h2 className="font-bold">Notifications</h2>
          </div>
          <ScrollArea className="flex-1">
            {unreadBySender.length > 0 ? (
              unreadBySender.map((item, idx) => (
                <div key={item.sender?.id || idx} className="p-3 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={getImageUrl(item.sender?.avatar_url)} />
                      <AvatarFallback className="bg-blue-600 text-white">
                        {(item.sender?.username || 'U')?.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{item.sender?.display_name || item.sender?.username || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">{item.unread_count || item.count} unread message(s)</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => {
                      pmWindows.openPMWindow({ 
                        id: item.sender?.id, 
                        username: item.sender?.username,
                        avatar_url: item.sender?.avatar_url
                      });
                    }}
                  >
                    View
                  </Button>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <p>No new notifications</p>
              </div>
            )}
          </ScrollArea>
        </div>
      )}

      {/* Members Modal */}
      <MembersModal
        open={showMembersModal}
        onOpenChange={setShowMembersModal}
        members={members}
        searchQuery={memberSearchQuery}
        onSearchChange={setMemberSearchQuery}
        user={user}
        onViewProfile={(memberId) => navigate(`/profile/${memberId}`)}
        onAddFriend={sendFriendRequest}
        onMuteUser={(member) => api.muteUser(member.id || member.user_id, selectedRoom?.id)}
        onKickUser={(member) => api.kickUser(member.id || member.user_id, selectedRoom?.id)}
        onBanUser={(member) => api.banUser(member.id || member.user_id, selectedRoom?.id)}
        onBlockUser={blockUser}
        onReportUser={(member) => openReportModal(member.id || member.user_id, member.username || member.user?.username)}
      />

      {/* Report User Modal */}
      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-orange-500" />
              Report User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Report {reportTarget?.username} for violating community guidelines
            </p>
            <div>
              <Label htmlFor="mobile-report-reason">Reason for Report</Label>
              <select
                id="mobile-report-reason"
                className="w-full mt-1 p-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">Select a reason...</option>
                <option value="spam">Spam or Advertising</option>
                <option value="harassment">Harassment or Bullying</option>
                <option value="hate_speech">Hate Speech</option>
                <option value="inappropriate">Inappropriate Content</option>
                <option value="impersonation">Impersonation</option>
                <option value="scam">Scam or Fraud</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setReportModalOpen(false);
                  setReportTarget(null);
                  setReportReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReportUser}
                disabled={!reportReason || reportSubmitting}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {reportSubmitting ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileChatLayout;

